import { useState, useCallback } from 'react';

import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';

import type { FetchOptions } from './fetchResponse';
import { useFetchData } from './useFetchData';
import type { TransformErrorType } from './handleError';
import { handleError } from './handleError';

type MutationResult<TData> = {
	data: TData | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	called: boolean;
};

type UseExperimentalMutationParams<TData, TVariables, TResponse> = FetchOptions<
	TVariables,
	TResponse
> & {
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void | Promise<void>;
	onError?: (error: ApolloErrorInterop) => Promise<void> | void;
	transformError?: TransformErrorType;
	maxRetries?: number;
	variables?: TVariables;
	update?: (cache: null, result: { data?: TData | null }) => void;
	optimisticResponse?: TData | ((vars: TVariables) => TData);
};

type MutationFunctionOptions<TData, TVariables> = {
	variables?: TVariables;
	update?: (cache: null, result: { data?: TData | null }) => void;
	optimisticResponse?: TData | ((vars: TVariables) => TData);
};

export function useExperimentalMutation<TData, TVariables, TResponse>({
	url,
	method,
	headers,
	body,
	credentials,
	parseResult,
	parseResponseOnSuccess,
	parseResponseOnError,
	onCompleted,
	onError,
	transformError,
	maxRetries = 0,
	variables: defaultVariables,
	update,
	optimisticResponse,
}: UseExperimentalMutationParams<TData, TVariables, TResponse>): [
	(options?: MutationFunctionOptions<TData, TVariables>) => Promise<{ data: TData | undefined }>,
	MutationResult<TData>,
] {
	const { fetchData } = useFetchData<TData, TVariables, TResponse>({
		url,
		method,
		headers,
		body,
		credentials,
		parseResult,
		parseResponseOnSuccess,
		parseResponseOnError,
		variables: defaultVariables,
	});

	const fetchDataWithRetry = useCallback(
		async (variables?: TVariables, count = 0): Promise<TData> => {
			try {
				return await fetchData(variables);
			} catch (error) {
				if (count < maxRetries && error?.response?.status === 409) {
					return fetchDataWithRetry(variables, ++count);
				}
				throw error;
			}
		},
		[maxRetries, fetchData],
	);

	return useExperimentalMutationBase<TData, TVariables>({
		fetchData: fetchDataWithRetry,
		onCompleted,
		onError,
		transformError,
		update,
		optimisticResponse,
	});
}

type UseExperimentalMutationBaseParams<TData, TVariables> = {
	fetchData: (variables?: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void | Promise<void>;
	onError?: (error: ApolloErrorInterop) => void;
	transformError?: TransformErrorType;
	update?: (cache: null, result: { data?: TData | null }) => void;
	optimisticResponse?: TData | ((vars: TVariables) => TData);
};

export function useExperimentalMutationBase<TData, TVariables>({
	fetchData,
	onCompleted,
	onError,
	transformError,
	update: defaultUpdate,
	optimisticResponse: defaultOptimisticResponse,
}: UseExperimentalMutationBaseParams<TData, TVariables>): [
	(options?: MutationFunctionOptions<TData, TVariables>) => Promise<{ data: TData | undefined }>,
	MutationResult<TData>,
] {
	const [result, setResult] = useState<MutationResult<TData>>({
		data: undefined,
		loading: false,
		error: undefined,
		called: false,
	});

	const mutate = useStableCallback(
		async (options: MutationFunctionOptions<TData, TVariables> = {}) => {
			const {
				variables,
				update: optionsUpdate,
				optimisticResponse: optionsOptimisticResponse,
			} = options;
			const update = optionsUpdate ?? defaultUpdate;

			setResult({ data: undefined, loading: true, error: undefined, called: true });

			const optimisticResponse = optionsOptimisticResponse ?? defaultOptimisticResponse;

			if (optimisticResponse) {
				const optimisticData =
					typeof optimisticResponse === 'function'
						? (optimisticResponse as (vars: TVariables) => TData)(variables ?? ({} as TVariables))
						: optimisticResponse;

				update?.(null, { data: optimisticData });
			}

			try {
				const data = await fetchData(variables);
				// The `update` function matches the Apollo useMutation API for compatibility
				// This hook does not implement a cache, so `cache` is always null
				update?.(null, { data });

				const newResult = { data, loading: false, error: undefined, called: true };
				setResult(newResult);

				await onCompleted?.(data);

				return { data: newResult.data };
			} catch (error) {
				// Rollback optimistic update on error
				if (optimisticResponse && update) {
					// Signal rollback by calling update with undefined data
					// Consumers should handle this to revert to the previous state
					update(null, { data: undefined });
				}

				const finalError = await handleError({ error, transformError, onError });
				const newResult = { data: undefined, loading: false, error: finalError, called: true };
				setResult(newResult);

				if (!onError) {
					throw finalError;
				}
				return { data: newResult.data };
			}
		},
	);

	return [mutate, result];
}
